import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AccountInfo, AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { from, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { MSAL_INSTANCE } from '../auth/msal-instance.token';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly msalInstance = inject(MSAL_INSTANCE);
  private readonly router = inject(Router);

  private readonly initializationPromise: Promise<void>;
  private readonly ise2eAuthenticationBypass = environment.e2eAuthenticationBypass === true;

  private accountSignal = signal<AccountInfo | null>(null);
  private loadingSignal = signal<boolean>(true);
  private errorSignal = signal<string | null>(null);
  private readonly _reauthInFlight = signal(false);
  private _silentRefreshInFlight: Observable<string | null> | null = null;

  readonly account = this.accountSignal.asReadonly();
  readonly isLoading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly reauthInFlight = this._reauthInFlight.asReadonly();

  readonly isAuthenticated = computed(() => this.accountSignal() !== null);

  constructor() {
    this.initializationPromise = this.initializeAuth();
  }

  async initializeAuth(): Promise<void> {
    try {
      this.loadingSignal.set(true);
      this.errorSignal.set(null);

      if (this.ise2eAuthenticationBypass) {
        this.accountSignal.set(this.createE2eAccount());
        return;
      }

      this.clearStaleInteractionStatus();

      const result = await this.msalInstance.handleRedirectPromise();
      if (result?.account) {
        this.setActiveAccount(result);
        this.navigateAfterLogin();
        return;
      }

      const account = this.getActiveAccount();
      if (account) {
        this.accountSignal.set(account);
      }
    } catch (error) {
      this.errorSignal.set(this.getErrorMessage(error));
      this.clearAuthState();
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /** Resolves once MSAL initialisation is complete; returns whether the user is authenticated. */
  async waitForAuthentication(): Promise<boolean> {
    await this.initializationPromise;
    return this.isAuthenticated();
  }

  triggerReauthentication(returnUrl?: string): void {
    if (this._reauthInFlight()) return;
    this._reauthInFlight.set(true);
    void this.signIn(returnUrl).finally(() => this._reauthInFlight.set(false));
  }

  async signIn(returnUrl?: string): Promise<void> {
    if (this.loadingSignal()) return;
    this.loadingSignal.set(true);
    try {
      await this.initializationPromise;

      this.clearStaleInteractionStatus();

      if (this.ise2eAuthenticationBypass) {
        this.accountSignal.set(this.createE2eAccount());
        if (returnUrl && this.isSameOriginRelativePath(returnUrl)) {
          await this.router.navigateByUrl(returnUrl);
        }
        return;
      }

      if (sessionStorage.getItem('msal.interaction.status')) {
        this.errorSignal.set('Sign-in already in progress. Please complete the existing sign-in.');
        return;
      }

      this.errorSignal.set(null);

      if (returnUrl) {
        sessionStorage.setItem('auth:returnUrl', returnUrl);
      }

      const rawScopes = environment.entra.scopes ?? [];
      const scopes = Array.isArray(rawScopes) ? rawScopes : [rawScopes];
      const effectiveScopes = scopes.length > 0 ? scopes : ['openid', 'profile', 'email'];

      await this.msalInstance.loginRedirect({
        scopes: effectiveScopes,
      });
    } catch (error) {
      this.errorSignal.set(this.getErrorMessage(error));
    } finally {
      this.loadingSignal.set(false);
    }
  }

  async signOut(): Promise<void> {
    try {
      this.loadingSignal.set(true);
      this.errorSignal.set(null);

      this.clearAuthState();
      if (this.ise2eAuthenticationBypass) {
        await this.router.navigate(['/']);
        return;
      }
      const postLogoutRedirectUri =
        environment.entra.postLogoutRedirectUri || window.location.origin;
      await this.msalInstance.logoutRedirect({ postLogoutRedirectUri });
    } catch (error) {
      this.errorSignal.set(this.getErrorMessage(error));
      this.clearAuthState();
      await this.router.navigate(['/login']);
    } finally {
      this.loadingSignal.set(false);
    }
  }

  getAccessToken(): Observable<string | null> {
    return this.acquireAccessToken(false, true);
  }

  refreshAccessTokenSilently(): Observable<string | null> {
    if (!this._silentRefreshInFlight) {
      this._silentRefreshInFlight = this.acquireAccessToken(true, false).pipe(
        finalize(() => { this._silentRefreshInFlight = null; }),
        shareReplay(1),
      );
    }
    return this._silentRefreshInFlight;
  }

  clearError(): void {
    this.errorSignal.set(null);
  }

  private setActiveAccount(result: AuthenticationResult): void {
    this.msalInstance.setActiveAccount(result.account);
    this.accountSignal.set(result.account);
  }

  private clearAuthState(): void {
    this.accountSignal.set(null);
  }

  private clearStaleInteractionStatus(): void {
    const interactionStatus = sessionStorage.getItem('msal.interaction.status');
    if (!interactionStatus) {
      return;
    }

    const hasAuthResponse =
      window.location.search.includes('code=') ||
      window.location.search.includes('error=') ||
      window.location.hash.includes('code=') ||
      window.location.hash.includes('error=');

    if (!hasAuthResponse) {
      sessionStorage.removeItem('msal.interaction.status');
    }
  }

  private getActiveAccount(): AccountInfo | null {
    if (this.ise2eAuthenticationBypass) {
      return this.accountSignal();
    }

    return this.msalInstance.getActiveAccount() || this.msalInstance.getAllAccounts()[0] || null;
  }

  private createE2eAccount(): AccountInfo {
    return {
      homeAccountId: 'e2e-user',
      environment: 'e2e',
      tenantId: 'e2e',
      username: 'e2e@test.local',
      localAccountId: 'e2e-user',
      name: 'E2E User',
      idTokenClaims: {},
    };
  }

  private navigateAfterLogin(): void {
    const returnUrl = sessionStorage.getItem('auth:returnUrl');
    sessionStorage.removeItem('auth:returnUrl');
    if (returnUrl && this.isSameOriginRelativePath(returnUrl)) {
      this.router.navigateByUrl(returnUrl);
    }
  }

  isSameOriginRelativePath(url: string): boolean {
    if (!url.startsWith('/') || url.startsWith('//')) {
      return false;
    }
    try {
      const resolved = new URL(url, window.location.origin);
      return resolved.origin === window.location.origin;
    } catch {
      return false;
    }
  }

  private getErrorMessage(error: unknown): string {
    if (typeof error === 'object' && error !== null) {
      const err = error as Record<string, unknown>;
      if (typeof err['errorMessage'] === 'string') return err['errorMessage'];
      if (typeof err['message'] === 'string') return err['message'];
    }
    return 'An unexpected authentication error occurred';
  }

  private acquireAccessToken(forceRefresh: boolean, allowInteractiveFallback: boolean): Observable<string | null> {
    if (this.ise2eAuthenticationBypass) {
      return of('e2e-bypass');
    }

    void this.initializationPromise;
    const account = this.getActiveAccount();
    if (!account) {
      return of(null);
    }

    if (!environment.entra.apiScope) {
      return of(null);
    }

    return from(
      this.msalInstance.acquireTokenSilent({
        scopes: [environment.entra.apiScope],
        account,
        forceRefresh,
      }),
    ).pipe(
      map((result) => result.accessToken),
      catchError((error) => {
        if (allowInteractiveFallback && error instanceof InteractionRequiredAuthError) {
          return from(
            this.msalInstance.acquireTokenRedirect({
              scopes: [environment.entra.apiScope],
              account,
            }),
          ).pipe(switchMap(() => of(null)));
        }

        if (error instanceof InteractionRequiredAuthError) {
          return of(null);
        }

        return of(null);
      }),
    );
  }
}
